import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DashboardOutlined,
  PeopleOutlined,
  CloudUploadOutlined,
  CalendarTodayOutlined,
  EventNoteOutlined,
  SettingsOutlined,
  MonitorHeartOutlined,
  NewReleasesOutlined,
  BusinessOutlined,
  FingerprintOutlined,
  LogoutOutlined,
  KeyboardArrowDown,
  MenuOpenOutlined,
  MedicalServicesOutlined,
  CampaignOutlined,
  BiotechOutlined,
  ExpandLess,
  ExpandMore,
  ListAltOutlined,
  BarChartOutlined,
} from '@mui/icons-material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import TuneIcon from '@mui/icons-material/Tune';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import TerminalIcon from '@mui/icons-material/Terminal';
import DevicesOutlined from '@mui/icons-material/DevicesOutlined';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasPermission } from '../lib/auth';
import i18n, { persistLanguage } from '../i18n';
import type { UserIdentity, OrgOption } from '../lib/types';
import { OnboardingLauncher } from './onboarding/OnboardingLauncher';
import { ContextHint } from './onboarding/ContextHint';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import { useContextHints } from '../hooks/useContextHints';
import { usePracticeScenariosList } from '../hooks/usePracticeScenarios';

declare const __APP_VERSION__: string;

/**
 * Fallback count for the "Cenários" sidebar badge when the backend feature
 * flag (`VITE_ESTUDIO_BACKEND_SCENARIOS_ENABLED`) is OFF, the query is still
 * loading, or the request errored. Mirrors the length of the hardcoded
 * `SCENARIOS` array in `src/pages/practice/scenes/ScenarioListScene.tsx`.
 *
 * Kept as a literal here (not imported) to avoid pulling the lazy-loaded
 * Practice route module into the shared `AppDrawer` chunk. Update this
 * constant whenever the legacy catalog is grown/shrunk until the array is
 * fully retired in favor of the backend list (EXK-235 Step 23+).
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported solely for AppDrawer.scenariosCountDrift.test.ts drift guard. Not consumed at runtime by any other module.
export const LEGACY_SCENARIO_COUNT = 41;

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  hidden?: boolean;
  secondary?: string;
  children?: NavChildItem[];
}

interface NavChildItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  hidden?: boolean;
  badge?: string;
  isSubheader?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

interface AppDrawerProps {
  user: UserIdentity | null;
  availableOrgs: OrgOption[];
  preAuthToken: string | null;
  switchingOrg: boolean;
  onSwitchOrg: (orgId: number) => void;
  onLogout: () => void;
  onManagePasskey?: () => void;
  onCollapse?: () => void;
}

function maskOrgName(orgId: number): string {
  return `HOPT-${String(orgId).padStart(3, '0')}`;
}

function getInitials(email: string): string {
  if (!email) return '??';
  const local = email.split('@')[0] ?? '';
  if (local.length === 0) return '??';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2 && parts[0]!.length > 0 && parts[1]!.length > 0) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

export function AppDrawer({
  user,
  availableOrgs,
  preAuthToken,
  switchingOrg,
  onSwitchOrg,
  onLogout,
  onManagePasskey,
  onCollapse,
}: AppDrawerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const onboarding = useOnboardingProgress();
  const hints = useContextHints(onboarding.role);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [language, setLanguage] = useState<'pt' | 'en' | 'es'>(
    (i18n.language?.split('-')[0] as 'pt' | 'en' | 'es') ?? 'pt',
  );
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    const init = new Set<string>();
    if (typeof window !== 'undefined') {
      if (window.location.pathname.startsWith('/settings')) init.add('/settings');
      if (window.location.pathname.startsWith('/practice')) init.add('/practice/scenarios');
    }
    return init;
  });

  function toggleExpanded(to: string): void {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(to)) {
        next.delete(to);
      } else {
        next.add(to);
      }
      return next;
    });
  }

  const canSwitchOrg = availableOrgs.length > 1 && preAuthToken;

  // Sidebar badge for "Cenários": when the Estudio backend flag is ON and the
  // list query has resolved, show the authoritative `meta.total`. During
  // loading or on error fall back to the legacy catalog length so the badge
  // never displays "--" or a stale `0`. When the flag is OFF the hook returns
  // `enabled: false` without firing a request — the fallback covers it too.
  // Rules of Hooks: must be invoked unconditionally; the hook itself is the
  // gate via `enabled` (cf. `usePracticeScenariosList`).
  const practiceScenariosList = usePracticeScenariosList();
  const practiceScenariosCount =
    practiceScenariosList.enabled &&
    !practiceScenariosList.isError &&
    practiceScenariosList.meta?.total != null
      ? practiceScenariosList.meta.total
      : LEGACY_SCENARIO_COUNT;

  // Auto-expand parent items when a child route is active
  useEffect(() => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      // Practice parent: /practice/scenarios
      if (location.pathname.startsWith('/practice')) {
        next.add('/practice/scenarios');
      }
      // Settings parent: /settings
      if (location.pathname.startsWith('/settings')) {
        next.add('/settings');
      }
      return next;
    });
  }, [location.pathname]);

  function handleLanguageChange(value: 'pt' | 'en' | 'es'): void {
    setLanguage(value);
    void i18n.changeLanguage(value);
    persistLanguage(value);
  }

  const sections: NavSection[] = useMemo(
    () => [
      {
        label: t('nav.sectionPrincipal'),
        items: [
          { label: t('nav.dashboard'), to: '/', icon: <DashboardOutlined /> },
          { label: t('nav.records'), to: '/patients', icon: <PeopleOutlined /> },
          { label: t('nav.surgicalLists'), to: '/surgical-lists', icon: <CalendarTodayOutlined /> },
          {
            label: t('nav.agenda'),
            to: '/agenda',
            icon: <EventNoteOutlined />,
            hidden: !hasPermission('surgical-lists.read'),
          },
          { label: t('nav.uploads'), to: '/uploads', icon: <CloudUploadOutlined /> },
          {
            label: t('nav.exams'),
            to: '/exam-sessions',
            icon: <BiotechOutlined />,
            hidden: !user?.isSuperAdmin || !hasPermission('exams.read'),
          },
        ],
      },
      {
        label: t('nav.sectionStudy'),
        items: [
          {
            label: t('nav.practice'),
            to: '/practice/scenarios',
            icon: <MedicalServicesOutlined />,
            hidden: !hasPermission('practice.read'),
            secondary: t('nav.practiceStatExs', { value: 78 }),
            children: [
              {
                label: t('nav.practiceScenarios'),
                to: '/practice/scenarios',
                icon: <ListAltOutlined />,
                badge: t('nav.practiceScenariosCount', { count: practiceScenariosCount }),
              },
              {
                label: t('nav.practiceAnalytics'),
                to: '/practice/analytics',
                icon: <BarChartOutlined />,
              },
              {
                label: t('nav.practiceHistory'),
                to: '/practice/history',
                icon: <HistoryIcon />,
              },
              {
                label: t('nav.preceptorDashboard'),
                to: '/preceptor/sessions',
                icon: <LeaderboardIcon />,
                hidden: !hasPermission('practice.read-others'),
              },
            ],
          },
        ],
      },
      {
        label: t('nav.sectionComms'),
        items: [
          { label: t('nav.notices'), to: '/notices', icon: <CampaignOutlined /> },
        ],
      },
      {
        label: t('nav.sectionAdmin'),
        items: [
          {
            label: t('nav.monitor'),
            to: '/monitor',
            icon: <MonitorHeartOutlined />,
            hidden: !hasPermission('users.manage'),
          },
          {
            label: t('nav.settings'),
            to: '/settings',
            icon: <SettingsOutlined />,
            hidden:
              !hasPermission('settings.read') &&
              !hasPermission('settings.write') &&
              !hasPermission('users.manage') &&
              !hasPermission('pii.config'),
            children: [
              // Equipe
              { label: t('settings.categories.team'), to: '', icon: <></>, isSubheader: true },
              { label: t('settings.tabs.members'), to: '/settings/members', icon: <PeopleAltIcon />, hidden: !hasPermission('members.read') },
              { label: t('settings.tabs.levels'), to: '/settings/levels', icon: <LeaderboardIcon />, hidden: !hasPermission('settings.read') },
              { label: t('settings.tabs.groups'), to: '/settings/groups', icon: <GroupWorkIcon />, hidden: !hasPermission('settings.read') },
              { label: t('settings.tabs.invites'), to: '/settings/invites', icon: <MailOutlineIcon />, hidden: !hasPermission('users.manage') },
              { label: t('settings.tabs.users'), to: '/settings/users', icon: <ManageAccountsIcon />, hidden: !user?.isSuperAdmin },
              // Operacional
              { label: t('settings.categories.operational'), to: '', icon: <></>, isSubheader: true },
              { label: t('settings.tabs.activities'), to: '/settings/activities', icon: <EventNoteIcon />, hidden: !hasPermission('settings.read') },
              { label: t('settings.tabs.absenceTypes'), to: '/settings/absenceTypes', icon: <EventBusyIcon />, hidden: !hasPermission('settings.read') },
              { label: t('settings.tabs.protocols'), to: '/settings/protocols', icon: <AssignmentReturnIcon />, hidden: !hasPermission('settings.read') },
              { label: t('settings.tabs.rotation'), to: '/settings/rotation', icon: <CalendarMonthIcon />, hidden: !hasPermission('rotation.read') },
              { label: t('settings.tabs.stages'), to: '/settings/stages', icon: <SwapHorizIcon />, hidden: !hasPermission('settings.read') },
              { label: t('settings.tabs.appointmentSubtypes'), to: '/settings/appointmentSubtypes', icon: <BookmarkBorderIcon />, hidden: !hasPermission('settings.read') },
              // Clínico
              { label: t('settings.categories.clinical'), to: '', icon: <></>, isSubheader: true },
              { label: t('settings.tabs.fieldDefinitions'), to: '/settings/fieldDefinitions', icon: <ListAltIcon />, hidden: !hasPermission('field-definitions.manage') },
              { label: t('settings.tabs.examTemplates'), to: '/settings/examTemplates', icon: <DescriptionIcon />, hidden: !hasPermission('exam-templates.manage') },
              // Sistema
              { label: t('settings.categories.system'), to: '', icon: <></>, isSubheader: true },
              { label: t('settings.tabs.preferences'), to: '/settings/preferences', icon: <TuneIcon /> },
              { label: t('settings.tabs.pii'), to: '/settings/pii', icon: <SecurityIcon />, hidden: !hasPermission('pii.config') },
              { label: t('settings.tabs.audit'), to: '/settings/audit', icon: <HistoryIcon />, hidden: !hasPermission('audit.read') },
              { label: t('settings.tabs.logs'), to: '/settings/logs', icon: <TerminalIcon />, hidden: !hasPermission('users.manage') },
              { label: t('settings.tabs.changelog'), to: '/settings/changelog', icon: <NewReleasesOutlined />, hidden: !user?.isSuperAdmin },
            ],
          },
          { label: t('nav.changelog'), to: '/changelog', icon: <NewReleasesOutlined />, hidden: !user?.isSuperAdmin },
        ],
      },
    ],
    [t, user, practiceScenariosCount],
  );

  return (
    <Stack
      sx={{
        height: '100%',
        bgcolor: 'rgba(8, 12, 18, 0.96)',
        backdropFilter: 'blur(16px)',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* ── Org Header ── */}
      {user && (
        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
          <Tooltip title={user.isSuperAdmin ? user.orgName : maskOrgName(user.orgId)} placement="right" arrow>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                borderRadius: 1.5,
                px: 1,
                py: 0.75,
                mx: -1,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'rgba(45, 212, 191, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <BusinessOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {user.isSuperAdmin ? user.orgName : maskOrgName(user.orgId)}
              </Typography>
              {onCollapse && (
                <IconButton
                  size="small"
                  onClick={onCollapse}
                  title={t('common.hideMenu')}
                  sx={{
                    flexShrink: 0,
                    opacity: 0.5,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <MenuOpenOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Stack>
          </Tooltip>

          {canSwitchOrg && (
            <Select
              size="small"
              value={user.orgId}
              onChange={(e) => onSwitchOrg(e.target.value as number)}
              disabled={switchingOrg}
              sx={{
                mt: 0.5,
                width: '100%',
                bgcolor: 'rgba(15, 23, 42, 0.5)',
                borderRadius: 1.5,
                '& .MuiSelect-select': { py: 0.5, px: 1.5, fontSize: 12 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(148, 163, 184, 0.1)',
                },
              }}
            >
              {availableOrgs.map((org) => (
                <MenuItem key={org.id} value={org.id} sx={{ fontSize: 13 }}>
                  {user?.isSuperAdmin ? org.name : maskOrgName(org.id)}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      )}

      <Divider sx={{ mx: 2 }} />

      {/* ── Navigation ── */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {sections.map((section) => {
          const visibleItems = section.items.filter((item) => !item.hidden);
          if (visibleItems.length === 0) return null;

          return (
            <List
              key={section.label}
              disablePadding
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    bgcolor: 'transparent',
                    color: 'text.disabled',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    lineHeight: '32px',
                    px: 3,
                    mt: 0.5,
                  }}
                >
                  {section.label}
                </ListSubheader>
              }
            >
              {visibleItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.has(item.to);
                const selected =
                  item.to === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.to);

                if (hasChildren) {
                  const visibleChildren = item.children!.filter((c) => !c.hidden);
                  const filteredChildren = visibleChildren.filter((child, idx) => {
                    if (!child.isSubheader) return true;
                    for (let i = idx + 1; i < visibleChildren.length; i++) {
                      if (visibleChildren[i]!.isSubheader) return false;
                      return true;
                    }
                    return false;
                  });
                  return (
                    <Box key={item.to}>
                      <ListItemButton
                        onClick={() => toggleExpanded(item.to)}
                        selected={selected && !isExpanded}
                        sx={{
                          mx: 1.5,
                          px: 1.5,
                          py: 0.6,
                          borderRadius: 1.5,
                          mb: 0.25,
                          borderLeft: '3px solid transparent',
                          transition: 'all 120ms ease',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(45, 212, 191, 0.10)',
                            borderLeftColor: 'primary.main',
                            color: 'primary.light',
                            '& .MuiListItemIcon-root': { color: 'primary.main' },
                          },
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(45, 212, 191, 0.14)' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          secondary={item.secondary}
                          primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }}
                          secondaryTypographyProps={{
                            fontSize: 10.5,
                            color: 'text.disabled',
                            sx: { mt: 0.1 },
                          }}
                        />
                        {isExpanded ? (
                          <ExpandLess sx={{ fontSize: 16, color: 'text.disabled' }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 16, color: 'text.disabled' }} />
                        )}
                      </ListItemButton>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List disablePadding>
                          {filteredChildren.map((child, idx) => {
                            if (child.isSubheader) {
                              return (
                                <ListSubheader
                                  key={`subheader-${idx}`}
                                  disableSticky
                                  sx={{
                                    bgcolor: 'transparent',
                                    color: 'text.secondary',
                                    fontSize: '0.7rem',
                                    lineHeight: '28px',
                                    textTransform: 'uppercase',
                                    pl: 6,
                                    letterSpacing: '0.06em',
                                    fontWeight: 700,
                                  }}
                                >
                                  {child.label}
                                </ListSubheader>
                              );
                            }
                            const childSelected =
                              child.to === '/'
                                ? location.pathname === '/'
                                : location.pathname === child.to;
                            return (
                              <ListItemButton
                                key={child.to}
                                component={Link}
                                to={child.to}
                                selected={childSelected}
                                sx={{
                                  mx: 1.5,
                                  pl: 4,
                                  pr: 1.5,
                                  py: 0.5,
                                  borderRadius: 1.5,
                                  mb: 0.25,
                                  borderLeft: '3px solid transparent',
                                  transition: 'all 120ms ease',
                                  '&.Mui-selected': {
                                    bgcolor: 'rgba(45, 212, 191, 0.10)',
                                    borderLeftColor: 'primary.main',
                                    color: 'primary.light',
                                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                                  },
                                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' },
                                  '&.Mui-selected:hover': { bgcolor: 'rgba(45, 212, 191, 0.14)' },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary', fontSize: 18 }}>
                                  {child.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.label}
                                  primaryTypographyProps={{
                                    fontSize: 12.5,
                                    fontWeight: childSelected ? 600 : 500,
                                  }}
                                />
                                {child.badge && (
                                  <Chip
                                    label={child.badge}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: 10,
                                      fontWeight: 600,
                                      bgcolor: childSelected
                                        ? 'rgba(45, 212, 191, 0.20)'
                                        : 'rgba(148, 163, 184, 0.10)',
                                      color: childSelected ? 'primary.light' : 'text.disabled',
                                      '& .MuiChip-label': { px: 0.75 },
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    </Box>
                  );
                }

                const navButton = (
                  <ListItemButton
                    key={item.to}
                    component={Link}
                    to={item.to}
                    selected={selected}
                    sx={{
                      mx: 1.5,
                      px: 1.5,
                      py: 0.6,
                      borderRadius: 1.5,
                      mb: 0.25,
                      borderLeft: '3px solid transparent',
                      transition: 'all 120ms ease',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(45, 212, 191, 0.10)',
                        borderLeftColor: 'primary.main',
                        color: 'primary.light',
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'rgba(45, 212, 191, 0.14)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: selected ? 600 : 500,
                      }}
                    />
                  </ListItemButton>
                );

                // Wrap surgical-lists nav with context hint
                if (item.to === '/surgical-lists' && onboarding.isEnabled) {
                  return (
                    <ContextHint
                      key={item.to}
                      hintId="nav.surgical_lists"
                      title={t('onboarding.hints.surgicalLists.title')}
                      body={t('onboarding.hints.surgicalLists.body')}
                      visible={hints.isVisible('nav.surgical_lists')}
                      onDismiss={hints.dismiss}
                      onDismissAll={hints.dismissAll}
                      onSeen={hints.markSeen}
                      placement="right"
                    >
                      {navButton}
                    </ContextHint>
                  );
                }

                return navButton;
              })}
            </List>
          );
        })}
      </Box>

      {/* ── Onboarding Launcher ── */}
      {onboarding.isEnabled && !onboarding.allComplete && onboarding.checklistDismissed && (
        <Box sx={{ px: 2.5, pb: 0.5 }}>
          <OnboardingLauncher
            percent={onboarding.percent}
            onClick={() => {
              if (location.pathname !== '/') navigate('/');
            }}
          />
        </Box>
      )}

      {/* ── User Footer ── */}
      {user && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              role="button"
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={Boolean(userMenuAnchor)}
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setUserMenuAnchor(e.currentTarget as HTMLElement);
                }
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: 1.5,
                px: 1,
                py: 0.75,
                transition: 'background-color 150ms',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: 'primary.dark',
                  color: 'primary.light',
                }}
              >
                {getInitials(user.email)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {user.email.split('@')[0] || '?'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}
                  >
                    {t(`common.role${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}`)}
                  </Typography>
                  <Box
                    sx={{
                      width: 3,
                      height: 3,
                      borderRadius: '50%',
                      bgcolor: 'text.disabled',
                    }}
                  />
                  <Select
                    size="small"
                    variant="standard"
                    value={language}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleLanguageChange(e.target.value as 'pt' | 'en' | 'es');
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disableUnderline
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'text.secondary',
                      '& .MuiSelect-select': { py: 0, pl: 0, pr: '14px !important' },
                      '& .MuiSvgIcon-root': { fontSize: 14, right: -2 },
                    }}
                  >
                    <MenuItem value="pt" sx={{ fontSize: 12 }}>PT</MenuItem>
                    <MenuItem value="en" sx={{ fontSize: 12 }}>EN</MenuItem>
                    <MenuItem value="es" sx={{ fontSize: 12 }}>ES</MenuItem>
                  </Select>
                </Stack>
              </Box>
              <KeyboardArrowDown
                sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
              />
            </Stack>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: 12,
                color: 'text.disabled',
                opacity: 0.7,
                pl: 1,
                mt: 0.5,
              }}
            >
              v{__APP_VERSION__}
            </Typography>
          </Box>

          {/* ── User Menu ── */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: 'rgba(18, 24, 38, 0.98)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid',
                  borderColor: 'divider',
                  minWidth: 200,
                },
              },
            }}
          >
            {onManagePasskey && (
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  onManagePasskey();
                }}
              >
                <ListItemIcon>
                  <FingerprintOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('common.managePasskey')}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setUserMenuAnchor(null);
                navigate('/settings/sessions');
              }}
            >
              <ListItemIcon>
                <DevicesOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('settings.sessions.menuLabel')}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setUserMenuAnchor(null);
                navigate('/settings/account');
              }}
            >
              <ListItemIcon>
                <FingerprintOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('account.menuLabel')}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setUserMenuAnchor(null);
                onLogout();
              }}
              sx={{
                color: 'error.main',
                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <LogoutOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('common.logout')}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
              />
            </MenuItem>
          </Menu>
        </>
      )}
    </Stack>
  );
}
